/**
 * Admin — Richieste Anagrafiche (Legal Change Requests review).
 *
 * Lists PENDING CustomerChangeRequest rows and lets ADMIN approve/reject.
 * Backend endpoints:
 *   GET  /api/admin/change-requests
 *   POST /api/admin/change-requests/{id}/approve
 *   POST /api/admin/change-requests/{id}/reject
 * ONLY legal fields are shown (logistics changes never enter a CR).
 */
import React, { useEffect, useState } from "react";
import { useAuth } from "../auth/AuthProvider";

const LEGAL_LABELS = {
  business_name: "Ragione sociale",
  vat_number: "Partita IVA",
  tax_code: "Codice fiscale",
  email: "Email",
  phone: "Telefono",
  legal_address: "Indirizzo sede legale",
  legal_zip_code: "CAP",
  legal_city: "Comune",
  legal_province: "Provincia",
  pec: "PEC",
  sdi_code: "Codice SDI",
};

const fmt = (v) => (v === null || v === undefined || v === "" ? "—" : String(v));

export default function AdminChangeRequestsPage() {
  const { apiGet, apiPost } = useAuth();
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState(null);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [statusFilter, setStatusFilter] = useState("PENDING");

  const load = async () => {
    setMsg(null);
    try {
      const q = statusFilter ? `?status=${encodeURIComponent(statusFilter)}` : "";
      const r = await apiGet(`/api/admin/change-requests${q}`);
      setRows(r.items || r || []);
    } catch (e) {
      setMsg({ tone: "err", text: String(e?.message || e) });
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);

  const action = async (approve) => {
    if (!selected) return;
    if (approve && !window.confirm("Confermi l'approvazione delle modifiche legali proposte?")) return;
    setBusy(true); setMsg(null);
    try {
      const path = approve
        ? `/api/admin/change-requests/${selected.id}/approve`
        : `/api/admin/change-requests/${selected.id}/reject`;
      await apiPost(path, { review_notes: notes || null });
      setMsg({ tone: "ok", text: approve ? "Richiesta approvata" : "Richiesta rifiutata" });
      setSelected(null); setNotes(""); await load();
    } catch (e) {
      setMsg({ tone: "err", text: String(e?.message || e) });
    } finally { setBusy(false); }
  };

  return (
    <div className="ds-page" data-testid="admin-change-requests-page" style={{ padding: 20 }}>
      <h1 style={{ marginTop: 0 }}>Richieste anagrafiche</h1>
      <p className="muted">Approva o rifiuta le modifiche dei dati legali proposte dai Clienti.
        I dati logistici (punti di consegna) NON richiedono approvazione.</p>

      <div style={{ display: "flex", gap: 10, alignItems: "center", margin: "12px 0" }}>
        <label>Filtro stato:</label>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                data-testid="cr-status-filter" className="ds-input" style={{ maxWidth: 180 }}>
          <option value="PENDING">In attesa</option>
          <option value="APPROVED">Approvate</option>
          <option value="REJECTED">Rifiutate</option>
          <option value="">Tutte</option>
        </select>
        <button className="ds-btn" onClick={load} data-testid="cr-reload-btn">Ricarica</button>
      </div>

      {msg && (
        <div data-testid="cr-msg" style={{
          padding: 10, marginBottom: 12, borderRadius: 6,
          background: msg.tone === "ok" ? "#e6ffed" : "#ffe6e6",
          color: msg.tone === "ok" ? "#0a5a2b" : "#8b0000",
        }}>{msg.tone === "err" ? "Impossibile caricare le richieste anagrafiche: " : ""}{msg.text}</div>
      )}

      <table className="ds-table" data-testid="cr-table" style={{ width: "100%" }}>
        <thead>
          <tr>
            <th>Data</th><th>Cliente</th><th>Codice</th><th>Stato</th>
            <th>Campi modificati</th><th></th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && !msg && (
            <tr><td colSpan={6} style={{ textAlign: "center", padding: 20 }} className="muted">
              Nessuna richiesta trovata.
            </td></tr>
          )}
          {rows.map((r) => {
            const changed = Object.keys(r.proposed_changes || {});
            return (
              <tr key={r.id} data-testid={`cr-row-${r.id}`}>
                <td>{r.created_at ? new Date(r.created_at).toLocaleString("it-IT") : ""}</td>
                <td>{r.customer?.business_name || r.customer_name || r.customer_id}</td>
                <td>{r.customer?.code || r.customer_code || "—"}</td>
                <td><span style={{ padding: "2px 8px", borderRadius: 4,
                             background: r.status === "PENDING" ? "#fff3cd"
                                       : r.status === "APPROVED" ? "#d4edda" : "#f8d7da",
                             color: r.status === "PENDING" ? "#856404"
                                  : r.status === "APPROVED" ? "#155724" : "#721c24" }}>
                  {r.status}
                </span></td>
                <td>{changed.length} campo/i</td>
                <td>
                  <button className="ds-btn" data-testid={`cr-open-${r.id}`}
                          onClick={() => { setSelected(r); setNotes(""); setMsg(null); }}>
                    Dettaglio
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {selected && (
        <div data-testid="cr-detail" style={{ marginTop: 20, padding: 16,
             border: "1px solid #ddd", borderRadius: 8, background: "#fafafa" }}>
          <h3 style={{ marginTop: 0 }}>
            Dettaglio richiesta — {selected.customer?.business_name || selected.customer_id}
          </h3>
          {selected.request_notes && (
            <p><strong>Note del Cliente:</strong> {selected.request_notes}</p>
          )}
          <table className="ds-table" data-testid="cr-diff-table" style={{ width: "100%", marginTop: 10 }}>
            <thead><tr><th>Campo</th><th>Valore attuale</th><th>Valore proposto</th></tr></thead>
            <tbody>
              {Object.entries(selected.proposed_changes || {}).map(([k, v]) => (
                <tr key={k} data-testid={`cr-diff-row-${k}`}>
                  <td><strong>{LEGAL_LABELS[k] || k}</strong></td>
                  <td style={{ color: "#666" }}>{fmt((selected.current_snapshot || {})[k])}</td>
                  <td style={{ color: "#0a5a2b", fontWeight: 600 }}>{fmt(v)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {selected.status === "PENDING" ? (
            <>
              <div style={{ marginTop: 12 }}>
                <label>Note revisione (opzionali):</label>
                <textarea data-testid="cr-review-notes" value={notes}
                          onChange={(e) => setNotes(e.target.value)} rows={2}
                          style={{ width: "100%", padding: 8, marginTop: 4 }} />
              </div>
              <div style={{ display: "flex", gap: 10, marginTop: 12 }}>
                <button className="ds-btn" style={{ background: "#28a745", color: "white" }}
                        onClick={() => action(true)} disabled={busy}
                        data-testid="cr-approve-btn">Approva</button>
                <button className="ds-btn" style={{ background: "#dc3545", color: "white" }}
                        onClick={() => action(false)} disabled={busy}
                        data-testid="cr-reject-btn">Rifiuta</button>
                <button className="ds-btn" onClick={() => setSelected(null)} disabled={busy}
                        data-testid="cr-close-btn">Chiudi</button>
              </div>
            </>
          ) : (
            <div style={{ marginTop: 12 }}>
              <p><em>Richiesta già {selected.status === "APPROVED" ? "approvata" : "rifiutata"}
                {selected.reviewed_at ? ` il ${new Date(selected.reviewed_at).toLocaleString("it-IT")}` : ""}.</em></p>
              {selected.review_notes && <p><strong>Note:</strong> {selected.review_notes}</p>}
              <button className="ds-btn" onClick={() => setSelected(null)}
                      data-testid="cr-close-btn">Chiudi</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
